import { ColorPicker, type ColorPickerProps } from 'antd';
import { type Color } from 'antd/es/color-picker';
import { useCallback } from 'react';
import styled from 'styled-components';
import { useInputController } from '../hooks/useInputController.ts';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility.tsx';
import { useFormItemEnabled } from '../item/useFormItemEnabled.ts';

// AntD, con size="large", rende lo swatch quadrato (controlHeight) allineato in
// alto nel trigger, facendolo sembrare tagliato. Qui lo ridimensioniamo a uno
// swatch più largo/basso. La className finisce sul `.ant-color-picker-trigger`,
// quindi i selettori colpiscono solo lo swatch del trigger e non l'anteprima nel
// popup.
//
// Lo swatch è `.ant-color-picker-color-block` per un colore valorizzato e
// `.ant-color-picker-clear` quando il valore è trasparente/vuoto: dimensioniamo
// entrambi allo stesso modo, altrimenti lo stato trasparente (quadrato a
// `controlHeight`) cambia l'altezza dell'input.
const StyledColorPicker = styled(ColorPicker)`
  &&& {
    align-items: center;
    padding-top: 7px;
  }

  &&& .ant-color-picker-color-block,
  &&& .ant-color-picker-clear {
    width: 60px;
    height: 26px;
  }

  /* La barra rossa del clear è tarata da AntD su uno swatch quadrato (40px a
     -45°): sul rettangolo 60x26 la riadattiamo alla diagonale reale così va da
     angolo ad angolo (angolo ≈ atan(26/60), lunghezza ≈ sqrt(60² + 26²)). Il
     colore resta quello di AntD. */
  &&& .ant-color-picker-clear::after {
    width: 66px;
    transform: rotate(-23.4deg);
  }
`;

export interface IColorPickerInputProps
  extends Omit<ColorPickerProps, 'name' | 'value' | 'onChange'> {
  name: string;
  onChange?: (value: string) => void;
}

/**
 * Componente di input per la selezione del colore.
 */
export function ColorPickerInput(props: IColorPickerInputProps) {
  const { name, onChange: onAfterChange, ...pickerProps } = props;

  const { enabled, visuallyDisabled } = useFormItemEnabled();

  const {
    field: { value, onChange },
  } = useInputController(name, { enabled });

  const handleChange = useCallback(
    (color: Color) => {
      const hex = color.toHexString();
      onChange(hex);
      onAfterChange?.(hex);
    },
    [onChange, onAfterChange],
  );

  const accessibility = useFormItemAccessibility();

  return (
    <StyledColorPicker
      style={{ width: '100%', justifyContent: 'flex-start' }}
      showText
      size="large"
      disabledAlpha
      format="hex"
      {...accessibility.attributes}
      {...pickerProps}
      disabled={visuallyDisabled}
      value={value ?? undefined}
      onChange={handleChange}
    />
  );
}
